import type {
  CreateWishlistAction,
  SetWishlistAction,
  WishlistItem,
} from '@packages/contracts/wishlist';
import {
  CreateWishlistActionSchema,
  SetWishlistActionSchema,
  WishlistAction,
} from '@packages/contracts/wishlist';
import { invariant } from '@packages/primitives/invariant';

export const WISHLIST_ITEM_LIMIT = 25;
export const WISHLIST_TITLE_LIMIT = 120;
export const WISHLIST_URL_LIMIT = 2048;
export const WISHLIST_NOTE_LIMIT = 240;

export interface WishlistFormValues {
  title: string;
  url: string;
  note: string;
}

export interface WishlistFormErrors {
  title?: string;
  url?: string;
  note?: string;
}

type WishlistFormValidationResult<TAction> =
  | { success: true; action: TAction }
  | { success: false; fieldErrors: WishlistFormErrors };

export function createWishlistFormValues(item: WishlistItem | null): WishlistFormValues {
  return {
    title: item?.title ?? '',
    url: item?.url ?? '',
    note: item?.note ?? '',
  };
}

export function buildCreateWishlistAction(
  values: WishlistFormValues,
): WishlistFormValidationResult<CreateWishlistAction> {
  const fieldErrors = getWishlistFormErrors(values);

  if (hasFieldErrors(fieldErrors)) {
    return { success: false, fieldErrors };
  }

  const action = {
    action: WishlistAction.CREATE,
    title: values.title.trim(),
    url: normalizeFormValue(values.url) ?? undefined,
    note: normalizeFormValue(values.note) ?? undefined,
  };

  const parsedAction = CreateWishlistActionSchema.safeParse(action);
  invariant(parsedAction.success, 'Create wishlist action should be valid after form validation.');

  return { success: true, action: parsedAction.data };
}

export function buildSetWishlistAction(
  itemId: string,
  values: WishlistFormValues,
): WishlistFormValidationResult<SetWishlistAction> {
  const fieldErrors = getWishlistFormErrors(values);

  if (hasFieldErrors(fieldErrors)) {
    return { success: false, fieldErrors };
  }

  const action = {
    action: WishlistAction.SET,
    id: itemId,
    title: values.title.trim(),
    url: normalizeFormValue(values.url),
    note: normalizeFormValue(values.note),
  };

  const parsedAction = SetWishlistActionSchema.safeParse(action);
  invariant(parsedAction.success, 'Set wishlist action should be valid after form validation.');

  return { success: true, action: parsedAction.data };
}

export function clearFieldError(
  fieldErrors: WishlistFormErrors,
  field: keyof WishlistFormErrors,
): WishlistFormErrors {
  const { [field]: _removed, ...rest } = fieldErrors;
  return rest;
}

function getWishlistFormErrors(values: WishlistFormValues): WishlistFormErrors {
  const fieldErrors: WishlistFormErrors = {};
  const title = values.title.trim();
  const url = values.url.trim();
  const note = values.note.trim();

  if (title.length === 0) {
    fieldErrors.title = 'Add a gift idea.';
  } else if (title.length > WISHLIST_TITLE_LIMIT) {
    fieldErrors.title = `Keep the title under ${WISHLIST_TITLE_LIMIT} characters.`;
  }

  if (url.length > WISHLIST_URL_LIMIT) {
    fieldErrors.url = `Keep the link under ${WISHLIST_URL_LIMIT} characters.`;
  } else if (url.length > 0 && !isHttpsUrl(url)) {
    fieldErrors.url = 'Enter a valid HTTPS URL.';
  }

  if (note.length > WISHLIST_NOTE_LIMIT) {
    fieldErrors.note = `Keep the note under ${WISHLIST_NOTE_LIMIT} characters.`;
  }

  return fieldErrors;
}

function hasFieldErrors(fieldErrors: WishlistFormErrors) {
  return Object.values(fieldErrors).some(Boolean);
}

function normalizeFormValue(value: string) {
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function isHttpsUrl(value: string) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
